import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useEffect } from "react";

const CookiePolicy = () => {
  useEffect(() => {
    document.title = 'Cookie Policy | AstroPet - Informazioni sui Cookie';
    
    // SEO meta tags
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Cookie Policy di AstroPet. Scopri come utilizziamo i cookie per migliorare la tua esperienza sul nostro social network per animali.');
    }
    
    // Canonical URL
    const canonical = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', `${window.location.origin}/cookie-policy`);
    if (!canonical.parentElement) document.head.appendChild(canonical);
  }, []);

  return (
    <div className="min-h-screen gradient-secondary">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center mb-8">
          <Link 
            to="/" 
            className="p-2 glass rounded-full hover:bg-white/10 transition-smooth mr-4"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </Link>
          <h1 className="text-3xl font-bold gradient-text">Cookie Policy</h1>
        </div>

        <Card className="glass border-white/20 mb-6">
          <CardHeader>
            <CardTitle className="text-white">Data di ultima modifica: 1 Settembre 2025</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 text-white/90">
            <section>
              <h2 className="text-2xl font-semibold gradient-text mb-4">1. Cosa sono i Cookie</h2>
              <p className="leading-relaxed">
                I cookie sono piccoli file di testo che vengono memorizzati sul tuo dispositivo quando visiti un sito web. 
                Ci permettono di riconoscere il tuo dispositivo e di migliorare la tua esperienza di navigazione.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold gradient-text mb-4">2. Tipologie di Cookie Utilizzati</h2>
              
              <div className="space-y-6">
                <div className="bg-white/5 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-primary mb-3">2.1 Cookie Strettamente Necessari</h3>
                  <p className="leading-relaxed mb-3">
                    Questi cookie sono essenziali per il funzionamento del sito e non possono essere disabilitati.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/20">
                          <th className="text-left p-2">Nome</th>
                          <th className="text-left p-2">Finalità</th>
                          <th className="text-left p-2">Durata</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-white/10">
                          <td className="p-2">auth_token</td>
                          <td className="p-2">Autenticazione utente</td>
                          <td className="p-2">Sessione</td>
                        </tr>
                        <tr className="border-b border-white/10">
                          <td className="p-2">csrf_token</td>
                          <td className="p-2">Protezione CSRF</td>
                          <td className="p-2">Sessione</td>
                        </tr>
                        <tr className="border-b border-white/10">
                          <td className="p-2">cookie_consent</td>
                          <td className="p-2">Memorizza preferenze cookie</td>
                          <td className="p-2">1 anno</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white/5 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-secondary mb-3">2.2 Cookie di Performance</h3>
                  <p className="leading-relaxed mb-3">
                    Raccolgono informazioni su come i visitatori utilizzano il sito (analytics). 
                    <strong> Richiedono il tuo consenso.</strong>
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/20">
                          <th className="text-left p-2">Nome</th>
                          <th className="text-left p-2">Finalità</th>
                          <th className="text-left p-2">Durata</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-white/10">
                          <td className="p-2">_ga</td>
                          <td className="p-2">Google Analytics - identificazione utente</td>
                          <td className="p-2">2 anni</td>
                        </tr>
                        <tr className="border-b border-white/10">
                          <td className="p-2">_ga_*</td>
                          <td className="p-2">Google Analytics - stato sessione</td>
                          <td className="p-2">2 anni</td>
                        </tr>
                        <tr className="border-b border-white/10">
                          <td className="p-2">user_preferences</td>
                          <td className="p-2">Preferenze di utilizzo</td>
                          <td className="p-2">6 mesi</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white/5 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-accent mb-3">2.3 Cookie di Funzionalità</h3>
                  <p className="leading-relaxed mb-3">
                    Permettono di ricordare le tue preferenze per migliorare l'esperienza. 
                    <strong> Richiedono il tuo consenso.</strong>
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/20">
                          <th className="text-left p-2">Nome</th>
                          <th className="text-left p-2">Finalità</th>
                          <th className="text-left p-2">Durata</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-white/10">
                          <td className="p-2">theme_preference</td>
                          <td className="p-2">Tema scuro/chiaro</td>
                          <td className="p-2">1 anno</td>
                        </tr>
                        <tr className="border-b border-white/10">
                          <td className="p-2">language</td>
                          <td className="p-2">Lingua preferita</td>
                          <td className="p-2">1 anno</td>
                        </tr>
                        <tr className="border-b border-white/10">
                          <td className="p-2">location_consent</td>
                          <td className="p-2">Consenso geolocalizzazione</td>
                          <td className="p-2">6 mesi</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white/5 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-orange-400 mb-3">2.4 Cookie di Marketing</h3>
                  <p className="leading-relaxed mb-3">
                    Utilizzati per mostrare pubblicità pertinenti e misurare l'efficacia delle campagne. 
                    <strong> Richiedono il tuo consenso esplicito.</strong>
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/20">
                          <th className="text-left p-2">Nome</th>
                          <th className="text-left p-2">Finalità</th>
                          <th className="text-left p-2">Durata</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-white/10">
                          <td className="p-2">_fbp</td>
                          <td className="p-2">Facebook Pixel</td>
                          <td className="p-2">3 mesi</td>
                        </tr>
                        <tr className="border-b border-white/10">
                          <td className="p-2">_gcl_au</td>
                          <td className="p-2">Google Ads conversions</td>
                          <td className="p-2">90 giorni</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold gradient-text mb-4">3. Cookie di Terze Parti</h2>
              <div className="space-y-4">
                <div className="bg-white/5 p-4 rounded-lg">
                  <h4 className="font-semibold text-primary mb-2">Google Analytics</h4>
                  <p className="text-sm leading-relaxed">
                    Utilizzato per analizzare l'utilizzo del sito. 
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" 
                       className="text-primary hover:underline ml-1">
                      Privacy Policy Google
                    </a>
                  </p>
                </div>
                <div className="bg-white/5 p-4 rounded-lg">
                  <h4 className="font-semibold text-primary mb-2">Facebook Pixel</h4>
                  <p className="text-sm leading-relaxed">
                    Per remarketing e analytics. 
                    <a href="https://www.facebook.com/privacy/explanation" target="_blank" rel="noopener noreferrer" 
                       className="text-primary hover:underline ml-1">
                      Privacy Policy Facebook
                    </a>
                  </p>
                </div>
                <div className="bg-white/5 p-4 rounded-lg">
                  <h4 className="font-semibold text-primary mb-2">Supabase</h4>
                  <p className="text-sm leading-relaxed">
                    Servizi di backend e database. 
                    <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" 
                       className="text-primary hover:underline ml-1">
                      Privacy Policy Supabase
                    </a>
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold gradient-text mb-4">4. Gestione delle Preferenze</h2>
              <div className="bg-gradient-to-r from-primary/20 to-secondary/20 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">Puoi gestire i cookie in diversi modi:</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Banner Cookie:</strong> Al primo accesso puoi scegliere quali categoria accettare</li>
                  <li><strong>Impostazioni Browser:</strong> Configura il tuo browser per bloccare o eliminare cookie</li>
                  <li><strong>Centro Preferenze:</strong> Accedi alle impostazioni privacy nel tuo profilo</li>
                  <li><strong>Opt-out Servizi:</strong> Utilizza gli strumenti di opt-out dei provider terzi</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold gradient-text mb-4">5. Strumenti di Controllo Browser</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white/5 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Chrome</h4>
                  <p className="text-sm">Impostazioni → Privacy e sicurezza → Cookie</p>
                </div>
                <div className="bg-white/5 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Firefox</h4>
                  <p className="text-sm">Opzioni → Privacy e sicurezza → Cookie e dati</p>
                </div>
                <div className="bg-white/5 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Safari</h4>
                  <p className="text-sm">Preferenze → Privacy → Gestisci dati siti web</p>
                </div>
                <div className="bg-white/5 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Edge</h4>
                  <p className="text-sm">Impostazioni → Privacy → Cookie e autorizzazioni</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold gradient-text mb-4">6. Conseguenze della Disabilitazione</h2>
              <div className="bg-orange-500/10 border border-orange-500/20 p-4 rounded-lg">
                <p className="leading-relaxed">
                  <strong>Attenzione:</strong> Disabilitando i cookie potrebbero non funzionare correttamente 
                  alcune funzionalità del sito, come il login automatico, le preferenze salvate e 
                  l'esperienza personalizzata.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold gradient-text mb-4">7. Aggiornamenti</h2>
              <p className="leading-relaxed">
                Questa Cookie Policy può essere aggiornata periodicamente. Ti informeremo di modifiche 
                sostanziali tramite il banner cookie o notifiche in-app.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold gradient-text mb-4">8. Contatti</h2>
              <div className="bg-white/5 p-4 rounded-lg">
                <p className="leading-relaxed">
                  Per domande sui cookie o per esercitare i tuoi diritti:<br/>
                  <strong>Email:</strong> privacy@astropet.com<br/>
                  <strong>DPO:</strong> dpo@astropet.com
                </p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CookiePolicy;